/**
 * Cart Store
 *
 * A Redux store that manages WooCommerce cart state for the frontend.
 * Follows WordPress data patterns with resolvers, selectors, and actions.
 * Similar to enrollment-store.ts but for cart operations.
 *
 * This store is used on the FRONTEND to manage cart state and check if coupons are applied.
 */
import { createReduxStore, register, dispatch, select } from '@wordpress/data';
import type { StoreDescriptor } from '@wordpress/data';
import { getCart } from '~/shared/queries/wc-store-api/queries';
import type { StoreAPICartResponse, CartCoupon } from '~/shared/queries/wc-store-api/types';

// ============================================================================
// Types
// ============================================================================

/**
 * State shape for the cart store
 */
interface CartState {
	readonly cart: StoreAPICartResponse | null;
	readonly isLoading: boolean;
	readonly error: string | null;
}

/**
 * Action types
 */
type SetCartAction = {
	type: 'SET_CART';
	cart: StoreAPICartResponse | null;
};

type SetLoadingAction = {
	type: 'SET_LOADING';
	isLoading: boolean;
};

type SetErrorAction = {
	type: 'SET_ERROR';
	error: string | null;
};

type CartAction = SetCartAction | SetLoadingAction | SetErrorAction;

// ============================================================================
// Implementation
// ============================================================================

const DEFAULT_STATE: CartState = {
	cart: null,
	isLoading: false,
	error: null,
};

/**
 * Actions - Synchronous state updates
 */
const actions = {
	setCart(cart: StoreAPICartResponse | null): SetCartAction {
		return {
			type: 'SET_CART',
			cart,
		};
	},

	setLoading(isLoading: boolean): SetLoadingAction {
		return {
			type: 'SET_LOADING',
			isLoading,
		};
	},

	setError(error: string | null): SetErrorAction {
		return {
			type: 'SET_ERROR',
			error,
		};
	},

	/**
	 * Refetch cart data - triggers resolver again
	 */
	*refetchCart() {
		// This is a generator action that triggers the resolver
		// Clear the resolution cache to force a re-fetch
		yield {
			type: 'INVALIDATE_RESOLUTION',
			selectorName: 'getCart',
			args: [],
		};
	},
};

/**
 * Selectors - Read state
 */
const selectors = {
	getCart(state: CartState): StoreAPICartResponse | null {
		return state.cart;
	},

	getCoupons(state: CartState): CartCoupon[] {
		return state.cart?.coupons || [];
	},

	isCouponApplied(state: CartState, code: string): boolean {
		return state.cart?.coupons?.some((c) => c.code === code) ?? false;
	},

	getCartItemsCount(state: CartState): number {
		return state.cart?.items_count ?? 0;
	},

	isLoading(state: CartState): boolean {
		return state.isLoading;
	},

	getError(state: CartState): string | null {
		return state.error;
	},
};

/**
 * Resolvers - Async data fetching
 * These are called by useSelect when data is needed
 */
const createResolvers = (
	storeDispatch: typeof import('@wordpress/data').dispatch,
	storeSelect: typeof import('@wordpress/data').select
) => {
	// Helper function to get store actions
	const getStoreActions = () => storeDispatch(CART_STORE_NAME) as typeof actions;

	return {
		/**
		 * Fetch cart data from the WooCommerce Store API
		 * This resolver is automatically called when getCart is used in useSelect
		 */
		async *getCart() {
			const { setLoading, setError, setCart } = getStoreActions();

			setLoading(true);
			setError(null);

			try {
				// Fetch cart data from WooCommerce Store API
				const cartData = await getCart();
				setCart(cartData);
			} catch (error: unknown) {
				console.error('[Leat Cart Store] Error fetching cart:', error);
				const errorMessage = error instanceof Error ? error.message : 'Failed to load cart';
				setError(errorMessage);
			} finally {
				setLoading(false);
			}
		},
	};
};

/**
 * Reducer - Handle state updates
 */
function reducer(state = DEFAULT_STATE, action: CartAction): CartState {
	switch (action.type) {
		case 'SET_CART':
			return {
				...state,
				cart: action.cart,
			};
		case 'SET_LOADING':
			return {
				...state,
				isLoading: action.isLoading,
			};
		case 'SET_ERROR':
			return {
				...state,
				error: action.error,
			};
		default:
			return state;
	}
}

// ============================================================================
// Store Registration
// ============================================================================

export const CART_STORE_NAME = 'leat/cart' as const;

const storeConfig = {
	reducer,
	actions,
	selectors,
	resolvers: createResolvers(dispatch, select),
};

export const cartStore: StoreDescriptor<typeof storeConfig> = createReduxStore(
	CART_STORE_NAME,
	storeConfig
);

register(cartStore);

// ============================================================================
// Type Exports
// ============================================================================

/**
 * Inferred types from implementation - no need to maintain separate interface definitions
 */
export type CartActions = typeof actions;
export type CartSelectors = typeof selectors;
export type CartResolvers = ReturnType<typeof createResolvers>;

/**
 * Complete store configuration type
 */
export interface CartStoreConfig {
	state: CartState;
	actions: CartActions;
	selectors: CartSelectors;
	resolvers: CartResolvers;
}
